import type {
  Composition,
  CompositionSection,
  Condition,
  DocumentReference,
  Encounter,
  MedicationRequest,
  Observation,
  Patient,
  Practitioner,
  Reference,
  ServiceRequest,
} from '@medplum/fhirtypes'
import { z } from 'zod'
import * as codeSystems from '#src/codeSystems.ts'
import {
  EntityType,
  ORDER_ENTITY_TYPES,
} from '#src/models/elation/entityType.ts'
import {
  BILLING_SECTION,
  CONDITION_SECTION,
  DOCUMENT_SECTION,
  OBSERVATION_SECTION,
  ORDER_SECTION,
  VisitNote as SeenVisitNote,
} from '#src/models/visitNote.ts'

// Define allowed categories and templates
const allowedCategories = [
  'Problem',
  'Past',
  'Family',
  'Social',
  'Instr',
  'PE',
  'ROS',
  'Med',
  'Data',
  'Assessment',
  'Test',
  'Tx',
  'Narrative',
  'Followup',
  'Reason',
  'Plan',
  'Objective',
  'Hpi',
  'Allergies',
  'Habits',
  'Assessplan',
  'Consultant',
  'Attending',
  'Dateprocedure',
  'Surgical',
  'Orders',
  'Referenced',
  'Procedure',
  'ElationNoteColOne',
  'ElationNoteColTwo',
  'ElationNoteOtherData',
] as const

export type AllowedCategories = (typeof allowedCategories)[number]

/** Bullet categories that may contain condition/diagnosis content */
export const CONDITION_BULLET_CATEGORIES = new Set<AllowedCategories>([
  'Assessplan',
  'Assessment',
  'Past',
  'Problem',
])

export const allowedTemplates = [
  'Simple',
  'SOAP',
  'Complete H&P (1 col)',
  'Complete H&P (2 col)',
  'Complete H&P (2 col A/P)',
  'twoCol.elationNote',
  'Pre-Op',
] as const

export type AllowedTemplates = (typeof allowedTemplates)[number]

// ---------------------------------------------------------------------------
// TipTap JSON node type helpers (plain objects — no TipTap library dependency)
// ---------------------------------------------------------------------------

interface TipTapTextNode {
  type: 'text'
  text: string
  marks?: { type: string }[]
}

interface TipTapMentionNode {
  type: 'mention'
  attrs: {
    reference: string
    display: string
    openId: null
  }
}

interface TipTapParagraphNode {
  type: 'paragraph'
  content?: (TipTapTextNode | TipTapMentionNode)[]
}

interface TipTapListItemNode {
  type: 'listItem'
  content: (TipTapParagraphNode | TipTapBulletListNode)[]
}

interface TipTapBulletListNode {
  type: 'bulletList'
  content: TipTapListItemNode[]
}

interface TipTapOrderedListNode {
  type: 'orderedList'
  content: TipTapListItemNode[]
}

interface TipTapHeadingNode {
  type: 'heading'
  attrs: { level: number }
  content: [TipTapTextNode]
}

type TipTapBlockNode =
  | TipTapHeadingNode
  | TipTapParagraphNode
  | TipTapBulletListNode
  | TipTapOrderedListNode

interface TipTapColumnNode {
  type: 'column'
  content: TipTapBlockNode[]
}

interface TipTapColumnBlockNode {
  type: 'columnBlock'
  attrs: { layout: 'equal' }
  content: [TipTapColumnNode, TipTapColumnNode]
}

type TipTapDocContent = TipTapBlockNode | TipTapColumnBlockNode

interface TipTapDocument {
  type: 'doc'
  content: TipTapDocContent[]
}

// ---------------------------------------------------------------------------
// Category display name mapping
// ---------------------------------------------------------------------------

const CATEGORY_DISPLAY_NAMES: Partial<Record<AllowedCategories, string>> = {
  Hpi: 'HPI',
  Assessplan: 'Assessment & Plan',
  Assessment: 'Assessment',
  Past: 'Past Medical History',
  Problem: 'Problem List',
  Family: 'Family History',
  Social: 'Social History',
  ROS: 'Review of Systems',
  PE: 'Physical Exam',
  Med: 'Medications',
  Data: 'Data',
  Test: 'Tests',
  Tx: 'Treatment',
  Instr: 'Care Plan',
  Followup: 'Follow Up',
  Objective: 'Objective',
  Allergies: 'Allergies',
  Habits: 'Habits',
  Consultant: 'Consultant',
  Attending: 'Attending',
  Dateprocedure: 'Date of Procedure',
  Surgical: 'Surgical History',
  Orders: 'Orders',
  Referenced: 'Documents Referenced',
  Procedure: 'Procedure',
  Plan: 'Plan',
  ElationNoteColOne: 'Column 1',
  ElationNoteColTwo: 'Column 2',
  ElationNoteOtherData: 'Other Data',
}

// ---------------------------------------------------------------------------
// Template-specific category sort order (lower = earlier in document)
// ---------------------------------------------------------------------------

/**
 * Each template defines its own category ordering. Categories not listed
 * receive DEFAULT_SORT_ORDER and appear after explicitly-ordered categories.
 */
const TEMPLATE_SORT_ORDERS: Partial<
  Record<AllowedTemplates, Partial<Record<AllowedCategories, number>>>
> = {
  Simple: {
    Narrative: 0,
    Instr: 1,
    Procedure: 2,
    Referenced: 3,
    Followup: 4,
  },
  SOAP: {
    Problem: 0,
    // Narrative is suppressed (no heading) but sits at position 1
    ROS: 2,
    Objective: 3,
    PE: 4,
    Assessment: 5,
    Plan: 6,
    Followup: 7,
    Referenced: 99,
  },
  'Complete H&P (1 col)': {
    Problem: 0,
    Hpi: 0,
    Allergies: 1,
    Past: 2,
    Social: 3,
    Habits: 4,
    Med: 5,
    Surgical: 6,
    ROS: 7,
    PE: 8,
    Data: 9,
    Assessplan: 10,
    Assessment: 10,
    Test: 11,
    Instr: 12,
    Followup: 13,
    Referenced: 99,
  },
  'Complete H&P (2 col)': {
    // Left column (within-column order)
    Problem: 0,
    Hpi: 0,
    Allergies: 1,
    Family: 2,
    Surgical: 3,
    Past: 3,
    Social: 4,
    Habits: 5,
    Med: 6,
    ROS: 7,
    // Right column (within-column order)
    PE: 0,
    Data: 1,
    Referenced: 2,
    Assessplan: 3,
    Assessment: 4,
    Test: 5,
    Tx: 6,
    Instr: 7,
    // Below (within-section order)
    Followup: 0,
  },
  'Complete H&P (2 col A/P)': {
    // Left column (within-column order)
    Problem: 0,
    Hpi: 0,
    Allergies: 1,
    Family: 2,
    Surgical: 3,
    Past: 3,
    Social: 4,
    Habits: 5,
    Med: 6,
    ROS: 7,
    // Right column (within-column order)
    PE: 0,
    Data: 1,
    Referenced: 2,
    Assessplan: 3,
    Assessment: 4,
    Test: 5,
    Tx: 6,
    Instr: 7,
    // Below (within-section order)
    Followup: 0,
  },
  'twoCol.elationNote': {
    // Left column
    Reason: 0,
    Hpi: 1,
    Allergies: 2,
    Med: 3,
    ROS: 4,
    Past: 5,
    Social: 6,
    Habits: 7,
    Family: 8,
    Surgical: 9,
    // Right column
    ElationNoteColTwo: 0,
    PE: 1,
    Assessplan: 2,
    Assessment: 3,
    Data: 4,
    Referenced: 5,
    Orders: 6,
    Instr: 7,
    Procedure: 8,
    // Below
    Followup: 0,
  },
  'Pre-Op': {
    Reason: 0,
    Allergies: 1,
    Past: 2,
    Surgical: 3,
    Med: 4,
    Assessplan: 5,
    Assessment: 6,
    Orders: 7,
    Instr: 8,
    Followup: 9,
    Referenced: 99,
  },
}

const DEFAULT_SORT_ORDER = 50

function getCategorySortOrder(
  category: AllowedCategories,
  template?: AllowedTemplates
): number {
  if (template) {
    const templateOrder = TEMPLATE_SORT_ORDERS[template]
    if (templateOrder && category in templateOrder) {
      return templateOrder[category] ?? DEFAULT_SORT_ORDER
    }
  }
  return DEFAULT_SORT_ORDER
}

// ---------------------------------------------------------------------------
// Excluded categories — completely omitted from TipTap content.
// Order bullets flow through the dedicated Orders metadata section only.
// ---------------------------------------------------------------------------

const EXCLUDED_CATEGORIES = new Set<AllowedCategories>(['Orders', 'Reason'])

// ---------------------------------------------------------------------------
// Suppressed categories — included in TipTap content but without a heading
// ---------------------------------------------------------------------------

const SUPPRESSED_CATEGORIES = new Set<AllowedCategories>(['Narrative'])

// ---------------------------------------------------------------------------
// Inline prefix categories — rendered as "Prefix: text" instead of a heading
// ---------------------------------------------------------------------------

const INLINE_PREFIX_CATEGORIES: Partial<Record<AllowedCategories, string>> = {
  Procedure: 'Proc:',
}

// ---------------------------------------------------------------------------
// TipTap node builders
// ---------------------------------------------------------------------------

function buildHeadingNode(text: string, level = 2): TipTapHeadingNode {
  return {
    type: 'heading',
    attrs: { level },
    content: [{ type: 'text', text }],
  }
}

function buildTextNode(text: string): TipTapTextNode {
  return { type: 'text', text }
}

function buildMentionNode(
  reference: string,
  display: string
): TipTapMentionNode {
  return {
    type: 'mention',
    attrs: { reference, display, openId: null },
  }
}

function buildParagraphWithText(text: string): TipTapParagraphNode {
  return { type: 'paragraph', content: [buildTextNode(text)] }
}

/**
 * Scans `text` for all keys in `conditionRefMap` and `orderRefMap` as substrings.
 * Returns an array of inline nodes (text and mention) that together represent the
 * full original text, with matched substrings replaced by mention nodes.
 *
 * Rules:
 * - Longest-match-wins at each position (greedy left-to-right)
 * - Case-insensitive matching, non-overlapping
 * - Empty text nodes are not emitted
 * - If no matches, returns a single text node (equivalent to the plain-text case)
 */
function splitTextWithMentions(
  text: string,
  conditionRefMap: Map<string, string>,
  orderRefMap: Map<string, string>
): (TipTapTextNode | TipTapMentionNode)[] {
  // Build a sorted list of [displayText, fhirReference] pairs, longest display first
  const entries: { display: string; reference: string }[] = []
  for (const [display, reference] of conditionRefMap) {
    entries.push({ display, reference })
  }
  for (const [display, reference] of orderRefMap) {
    entries.push({ display, reference })
  }
  // Sort longest first so longest-match-wins at each position
  entries.sort((a, b) => b.display.length - a.display.length)

  if (entries.length === 0) {
    return [buildTextNode(text)]
  }

  const result: (TipTapTextNode | TipTapMentionNode)[] = []
  let cursor = 0

  while (cursor < text.length) {
    let matched = false

    for (const { display, reference } of entries) {
      const slice = text.slice(cursor, cursor + display.length)
      if (display.length > 0 && slice.toLowerCase() === display) {
        // Emit mention node with original-cased text from the source
        result.push(buildMentionNode(reference, slice))
        cursor += display.length
        matched = true
        break
      }
    }

    if (!matched) {
      // Advance one character; extend the last text node if possible
      const last = result[result.length - 1]
      if (last && last.type === 'text') {
        last.text += text[cursor]
      } else {
        result.push(buildTextNode(text[cursor]))
      }
      cursor++
    }
  }

  return result
}

function buildBulletList(items: string[]): TipTapBulletListNode {
  return {
    type: 'bulletList',
    content: items.map((text) => ({
      type: 'listItem' as const,
      content: [buildParagraphWithText(text)] as [TipTapParagraphNode],
    })),
  }
}

/**
 * Builds a bullet list where each item has an italic label followed by plain
 * text value. Used for ROS/PE checklists where Elation renders labels in
 * italics (e.g., "*General:* Denies fever...").
 */
function buildChecklistBulletList(
  items: { name: string; value: string }[]
): TipTapBulletListNode {
  return {
    type: 'bulletList',
    content: items.map((item) => ({
      type: 'listItem' as const,
      content: [
        {
          type: 'paragraph',
          content: [
            {
              type: 'text',
              text: `${item.name}:`,
              marks: [{ type: 'italic' }],
            } satisfies TipTapTextNode,
            buildTextNode(` ${item.value}`),
          ],
        } satisfies TipTapParagraphNode,
      ] as [TipTapParagraphNode],
    })),
  }
}

/**
 * To validate bullets, there are 3 rules:
 * 1. Each template supports only certain categories of bullets, and each category of bullet
 * is configured further to support children or multiple parent bullets of that category
 * 2. Bullet nesting is only allowed if the category supports children and only 1 layer deep
 * 3. If a category does not support multiple parent bullets, then only 1 bullet of that category
 * is allowed as a parent bullet in the note
 */
export const categoryRulesByTemplate: Record<
  AllowedTemplates,
  {
    [key in AllowedCategories]?: {
      supportsMultipleParentBulletsOfCategory: boolean
      supportsChildren: boolean
    }
  }
> = {
  Simple: {
    Followup: {
      supportsMultipleParentBulletsOfCategory: false,
      supportsChildren: false,
    },
    Instr: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: true,
    },
    Narrative: {
      supportsMultipleParentBulletsOfCategory: false,
      supportsChildren: false,
    },
    Orders: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Procedure: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Reason: {
      supportsMultipleParentBulletsOfCategory: false,
      supportsChildren: false,
    },
    Referenced: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
  },
  SOAP: {
    Assessment: {
      supportsMultipleParentBulletsOfCategory: false,
      supportsChildren: false,
    },
    Followup: {
      supportsMultipleParentBulletsOfCategory: false,
      supportsChildren: false,
    },
    Instr: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: true,
    },
    Objective: {
      supportsMultipleParentBulletsOfCategory: false,
      supportsChildren: false,
    },
    Orders: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    PE: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Plan: {
      supportsMultipleParentBulletsOfCategory: false,
      supportsChildren: false,
    },
    Problem: {
      supportsMultipleParentBulletsOfCategory: false,
      supportsChildren: false,
    },
    Procedure: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Reason: {
      supportsMultipleParentBulletsOfCategory: false,
      supportsChildren: false,
    },
    Referenced: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    ROS: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
  },
  'Complete H&P (1 col)': {
    Allergies: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: true,
    },
    Assessplan: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: true,
    },
    Data: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Family: {
      supportsMultipleParentBulletsOfCategory: false,
      supportsChildren: false,
    },
    Followup: {
      supportsMultipleParentBulletsOfCategory: false,
      supportsChildren: false,
    },
    Habits: {
      supportsMultipleParentBulletsOfCategory: false,
      supportsChildren: false,
    },
    Hpi: {
      supportsMultipleParentBulletsOfCategory: false,
      supportsChildren: false,
    },
    Instr: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: true,
    },
    Med: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: true,
    },
    Orders: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Past: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: true,
    },
    PE: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Procedure: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Reason: {
      supportsMultipleParentBulletsOfCategory: false,
      supportsChildren: false,
    },
    Referenced: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    ROS: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Social: {
      supportsMultipleParentBulletsOfCategory: false,
      supportsChildren: false,
    },
    Surgical: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: true,
    },
  },
  'Complete H&P (2 col)': {
    Allergies: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Assessment: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: true,
    },
    Data: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Family: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Followup: {
      supportsMultipleParentBulletsOfCategory: false,
      supportsChildren: false,
    },
    Habits: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Instr: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: true,
    },
    Med: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Past: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    PE: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Problem: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: true,
    },
    Procedure: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Reason: {
      supportsMultipleParentBulletsOfCategory: false,
      supportsChildren: false,
    },
    ROS: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Social: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Surgical: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Test: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Tx: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
  },
  'Complete H&P (2 col A/P)': {
    Allergies: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Assessplan: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: true,
    },
    Data: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Family: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Followup: {
      supportsMultipleParentBulletsOfCategory: false,
      supportsChildren: false,
    },
    Habits: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Instr: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: true,
    },
    Med: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Orders: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Past: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    PE: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Problem: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: true,
    },
    Procedure: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Reason: {
      supportsMultipleParentBulletsOfCategory: false,
      supportsChildren: false,
    },
    ROS: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Social: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Surgical: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
  },
  'twoCol.elationNote': {
    Allergies: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: true,
    },
    Assessplan: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: true,
    },
    Data: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    ElationNoteColOne: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: true,
    },
    ElationNoteColTwo: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: true,
    },
    ElationNoteOtherData: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: true,
    },
    Family: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Followup: {
      supportsMultipleParentBulletsOfCategory: false,
      supportsChildren: false,
    },
    Habits: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Hpi: {
      supportsMultipleParentBulletsOfCategory: false,
      supportsChildren: false,
    },
    Instr: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: true,
    },
    Med: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: true,
    },
    Orders: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    PE: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Past: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: true,
    },
    Procedure: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Reason: {
      supportsMultipleParentBulletsOfCategory: false,
      supportsChildren: false,
    },
    Referenced: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    ROS: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Social: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Surgical: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
  },
  'Pre-Op': {
    Allergies: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: true,
    },
    Assessplan: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: true,
    },
    Followup: {
      supportsMultipleParentBulletsOfCategory: false,
      supportsChildren: false,
    },
    Instr: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: true,
    },
    Med: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: true,
    },
    Orders: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Past: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: true,
    },
    Reason: {
      supportsMultipleParentBulletsOfCategory: false,
      supportsChildren: false,
    },
    Referenced: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: false,
    },
    Surgical: {
      supportsMultipleParentBulletsOfCategory: true,
      supportsChildren: true,
    },
  },
}

const validChecklistTypesAndNames = {
  pe: new Set([
    'General',
    'Eyes',
    'HENT',
    'Neck',
    'Resp',
    'CVS',
    'Breast',
    'Abdom',
    'GU',
    'MSS',
    'NS',
    'Skin',
    'Lymph',
    'Psych',
    'Pelvic',
    'Rectal',
  ]),
  ros: new Set([
    'General',
    'Diet',
    'Exercise',
    'Eyes',
    'HENT',
    'Resp',
    'CVS',
    'Breast',
    'GI',
    'GU',
    'Gyne',
    'MSS',
    'NS',
    'Skin',
    'Hemo',
    'Endoc',
    'Psych',
  ]),
}

/**
 * This is an awkward way to define the schema, but it's the only way to define a recursive schema
 * with strong typing in TypeScript. See https://github.com/colinhacks/zod#recursive-types
 */
const baseBulletSchema = z.object({
  category: z.enum(allowedCategories),
  text: z.string().max(16384),
  version: z.number(),
  sequence: z.number(),
  author: z
    .number()
    .describe(
      'This is the physicians `user_id`, not to be confused with the physicians `id`'
    ),
  updated_date: z.string(),
  replaced_by_edit: z.number().nullable(),
  replaced_by: z.number().nullable(),
  edit: z.number().nullable(),
  deleted_date: z.string().nullable(),
  note_document: z
    .object({
      id: z.number(),
      document: z.object({
        authoring_practice: z.number(),
        chart_date: z.string(),
        created_date: z.string(),
        deleted_date: z.string().nullable(),
        document_date: z.string(),
        document_type: z.nativeEnum(EntityType).nullable(),
        id: z.number(),
        last_modified: z.string().nullable(),
        patient: z.number(),
        sign_date: z.string(),
        signed_by: z.number(),
      }),
      summary: z.string().nullable(),
    })
    .nullable(),
  note_item: z
    .object({
      id: z.number(),
      item: z.object({
        id: z.number(),
        created_date: z.string(),
        deleted_date: z.string().nullable(),
        patient: z.number(),
        type: z.string(),
        is_confidential: z.boolean(),
        itemType: z.string().optional(),
      }),
    })
    .nullable(),
  handout: z.number().nullable(),
})

// intermediate type; necessary to allow TypeScript to properly type the children property
export type Bullet = z.infer<typeof baseBulletSchema> & {
  // Not visible in the Find Visit Notes endpoint
  // https://arc.net/l/quote/kopzjcbw
  children?: Bullet[] | null
}

export const bulletSchema: z.ZodType<Bullet> = baseBulletSchema.extend({
  children: z
    .lazy(() => bulletSchema.array().optional().nullable())
    .optional()
    .nullable(),
})

export const checklistItemSchema = z.object({
  name: z.string(),
  value: z.string(),
  sequence: z.number(),
})

export const editSchema = z.object({
  visit_note: z.number(),
  create_user: z.number(),
  created_date: z.string(),
  previous_note_type: z.string().nullable().optional(),
  previous_note_time: z.string().nullable().optional(),
  new_note_type: z.string().nullable().optional(),
  new_note_time: z.string().nullable().optional(),
  previous_prev_weight: z.number().nullable().optional(),
  new_prev_weight: z.number().nullable().optional(),
  previous_prev_bmi: z.number().nullable().optional(),
  new_prev_bmi: z.number().nullable().optional(),
  previous_prev_time: z.string().nullable().optional(),
  new_prev_time: z.string().nullable().optional(),
  previous_bmi: z.number().nullable().optional(),
  new_bmi: z.number().nullable().optional(),
})

export const signatureSchema = z.object({
  user: z.number(),
  user_name: z.string(),
  signed_date: z.string(),
  role: z.string(),
  comments: z.string().nullable(),
})

export const visitNoteSchema = z
  .object({
    id: z.number().int().nullable().optional(), // read-only
    bullets: z.array(bulletSchema),
    checklists: z.object({
      /* Physical exam */
      pe: z.array(checklistItemSchema).optional(),
      /* Review of systems */
      ros: z.array(checklistItemSchema).optional(),
    }),
    edits: z.array(editSchema),
    signatures: z.array(signatureSchema).optional().nullable(),
    type: z.string().max(50),
    template: z.enum(allowedTemplates),
    amendment_request: z.string().nullable(),
    patient: z.number(),
    physician: z.number(),
    practice: z.number(),
    document_date: z.string(),
    chart_date: z.string(),
    clinical_summary_link: z.string(),
    visit_summary_link: z.string(),
    signed_date: z.string().nullable(),
    signed_by: z.number().nullable(),
    created_date: z.string(),
    last_modified: z.string(),
    deleted_date: z.string().nullable(),
    tags: z.array(z.object({})),
    confidential: z.boolean(),
  })
  .superRefine((visitNote, ctx) => {
    const { template, bullets } = visitNote

    const rulesByCategory = categoryRulesByTemplate[template]

    const categoriesCount: {
      [key in AllowedCategories]?: number
    } = {}

    bullets.forEach((bullet, index) => {
      const bulletRules = rulesByCategory[bullet.category]
      // ensure bullet category is allowed in the template
      if (!bulletRules) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Category ${bullet.category} not allowed in template ${template}`,
          path: ['bullets', index],
        })
        // early return because we can't validate further if the category is not allowed
        return
      }

      const newCategoryCount = (categoriesCount[bullet.category] ?? 0) + 1
      // ensure only 1 bullet of a category is allowed if the category does not support multiple parent bullets
      if (
        newCategoryCount > 1 &&
        !bulletRules.supportsMultipleParentBulletsOfCategory
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Category ${bullet.category} does not support multiple parent bullets`,
          path: ['bullets', index],
        })
      }

      categoriesCount[bullet.category] = newCategoryCount

      if ((bullet.children?.length ?? 0) > 0) {
        // ensure category supports children
        if (!bulletRules.supportsChildren) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Category ${bullet.category} does not support children`,
            path: ['bullets', index],
          })
        }

        // ensure only 1 layer of nesting is allowed
        bullet.children?.forEach((child, index) => {
          if ((child.children?.length ?? 0) > 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Only 1 layer of nesting is allowed`,
              path: ['bullets', index, 'children', index],
            })
          }
        })
      }
    })

    // validate the checklist items
    Object.entries(visitNote.checklists).forEach(
      ([untypedChecklistType, checklistItems]) => {
        const checklistType =
          untypedChecklistType as keyof typeof validChecklistTypesAndNames

        // ensure every checklist type is valid
        const validChecklistItemNames =
          validChecklistTypesAndNames[checklistType]
        if (!validChecklistItemNames) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Checklist type ${checklistType} is not valid`,
            path: ['checklists', checklistType],
          })
        }

        // ensure every checklist item name is valid for the checklist type
        checklistItems.forEach((item, index) => {
          if (!validChecklistItemNames.has(item.name)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Checklist item name ${item.name} is not valid for type ${checklistType}`,
              path: ['checklists', checklistType, index],
            })
          }
        })
      }
    )
  })

export type VisitNote = z.infer<typeof visitNoteSchema>

export interface BillingSectionData {
  bills: { id: number; dxs: string[]; cpt: string; modifiers: string[] }[]
  billingStatus: string
  billingDate: string
  refNumber: string
  serviceDate: string
}

export function fromFhir(_composition: Composition): VisitNote {
  // We don't currently have a use case for converting a FHIR Composition to an Elation Visit Note
  // as we only sync uni-directionally from Elation
  throw new Error('Not implemented')
}

/**
 * Builds a TipTap JSON document from the visit note bullets and checklists.
 *
 * Structure:
 * - Bullets are grouped by category and sorted by CATEGORY_SORT_ORDER
 * - Each group gets a heading node (level 2) unless the category is suppressed
 * - Each bullet becomes a paragraph — either a mention node (if matched to a
 *   condition/order reference) or plain text
 * - Child bullets become a bulletList after the parent paragraph
 * - ROS and PE checklists are appended as heading + bullet list of "Name: Value" pairs
 */
/** A/P categories where ICD-10 codes should be rendered below the condition mention */
const AP_CONDITION_CATEGORIES = new Set<AllowedCategories>([
  'Assessplan',
  'Assessment',
])

/**
 * Categories whose bullets are rendered as an ordered (numbered) list.
 * Each bullet becomes a numbered list item with its child bullets nested inside.
 *
 * Only applies to H&P templates. SOAP and Simple render all categories as
 * plain text — the content is narrative, not structured lists.
 */
/**
 * Categories that are numbered in H&P templates. SOAP and Simple templates
 * don't use numbered lists at all. Past and Med are numbered in H&P but not
 * in SOAP/Simple where they're single-narrative blocks.
 */
const NUMBERED_CATEGORIES = new Set<AllowedCategories>([
  'Problem',
  'Assessplan',
  'Assessment',
  'Instr',
  'Past',
  'Med',
  'Allergies',
])

const UNNUMBERED_TEMPLATES = new Set<AllowedTemplates>(['SOAP', 'Simple'])

/**
 * Elation prepends order status tags directly onto medication bullet text
 * (e.g., "NewAzithromycin", "Doc'd ChangePrevnar"). Used to insert a space
 * after the prefix so downstream rendering can recognize and format it.
 */
const ELATION_ORDER_PREFIXES =
  /^(?:New|Doc'd Change|D\/C|Refill|Reorder|Discontinue)(?! )/

/**
 * Categories whose bullets are rendered as an unordered (bullet) list.
 * Similar to NUMBERED_CATEGORIES but produces bulletList instead of orderedList.
 */
const BULLETED_CATEGORIES = new Set<AllowedCategories>([
  'Test',
  'Tx',
  'Data',
  'Referenced',
])

/**
 * Templates that use a two-column layout. For these templates, categories are
 * routed to left, right, or below columns based on explicit category membership.
 */
const TWO_COL_TEMPLATES = new Set<AllowedTemplates>([
  'Complete H&P (2 col)',
  'Complete H&P (2 col A/P)',
  'twoCol.elationNote',
])

/**
 * Categories placed in the right column of two-column templates.
 * Everything NOT in this set and NOT in TWO_COL_BELOW_CATEGORIES goes left.
 * PE checklists (kind: 'checklist', category: 'PE') are also routed right;
 * ROS checklists (category: 'ROS') fall through to the left column.
 */
const TWO_COL_RIGHT_CATEGORIES = new Set<AllowedCategories>([
  'PE',
  'Data',
  'Test',
  'Tx',
  'Referenced',
  'Assessplan',
  'Assessment',
  'Instr',
  'ElationNoteColTwo',
])

/**
 * Categories in the right column that appear under a "Plan" H1 header,
 * below the "Objective" section. Everything else in the right column
 * falls under the "Objective" header.
 */
const TWO_COL_PLAN_CATEGORIES = new Set<AllowedCategories>([
  'Test',
  'Tx',
  'Instr',
])

/**
 * Categories rendered full-width below the two-column area.
 */
const TWO_COL_BELOW_CATEGORIES = new Set<AllowedCategories>([
  'Followup',
  'ElationNoteOtherData',
])

interface BuiltTipTapDocument {
  doc: TipTapDocument
  firstBulletText?: string
}

function buildTipTapDocument({
  bullets,
  checklists,
  conditionRefMap,
  orderRefMap,
  documentRefMap,
  conditionIcdMap,
  template,
}: {
  bullets: Bullet[]
  checklists: VisitNote['checklists']
  /** Map from condition display text → FHIR reference string (e.g. "Condition/uuid") */
  conditionRefMap: Map<string, string>
  /** Map from order display text → FHIR reference string (e.g. "ServiceRequest/uuid") */
  orderRefMap: Map<string, string>
  /** Map from document display text → FHIR reference string (e.g. "DocumentReference/uuid") */
  documentRefMap: Map<string, string>
  /** Map from condition display text → ICD-10 code string (e.g. "K51.90"), used to emit a bold code line in A/P sections */
  conditionIcdMap: Map<string, string>
  /** Template name for template-specific category ordering */
  template?: AllowedTemplates
}): BuiltTipTapDocument {
  let firstBulletText: string | undefined

  // Group bullets by category, preserving insertion order within each group.
  // Include bullets with note_document.summary as fallback for empty text.
  const grouped = new Map<AllowedCategories, Bullet[]>()
  for (const bullet of bullets) {
    if (!bullet.text && !bullet.note_document?.summary) {
      continue
    }
    const existing = grouped.get(bullet.category)
    if (existing) {
      existing.push(bullet)
    } else {
      grouped.set(bullet.category, [bullet])
    }
  }

  // For twoCol.elationNote, extract title from the first HPI bullet's full text
  // (joined lines, stripped of leading number). The default firstBulletText logic
  // only takes the first line, which gives "1. Chief Complaint (CC):" — not useful.
  if (template === 'twoCol.elationNote') {
    const hpiBullets = grouped.get('Hpi')
    const firstHpi = hpiBullets?.[0]?.text?.trim()
    if (firstHpi) {
      firstBulletText = firstHpi
        .split('\n')
        .map((l) => l.trim())
        .join(' ')
        .replace(/^\d+\.?\s*/, '')
        .replace(/[^a-zA-Z0-9)]+$/, '')
    }
  }

  // For twoCol.elationNote, condense verbose Med bullets from structured
  // "- Name: X\n- Directions: Y" format to "X sig: Y" so mention matching
  // works (orderRefMap keys are medication names, not verbose text).
  if (template === 'twoCol.elationNote') {
    const medBullets = grouped.get('Med')
    if (medBullets) {
      for (const bullet of medBullets) {
        if (!bullet.text) {
          continue
        }
        const nameMatch = /- Name:\s*(.+)/m.exec(bullet.text)
        const dirMatch = /- Directions:\s*(.+)/m.exec(bullet.text)
        const name = nameMatch?.[1]?.trim()
        const directions = dirMatch?.[1]?.trim()
        if (name) {
          bullet.text = directions ? `${name} sig: ${directions}` : name
        }
      }
    }
  }

  // Build a unified list of sortable entries: bullet categories + ROS/PE checklists.
  // Each entry is either a bullet category (rendered as heading + paragraphs)
  // or a checklist (rendered as heading + italic-label bullet list).
  type SortableEntry =
    | { kind: 'bullets'; category: AllowedCategories }
    | {
        kind: 'checklist'
        category: 'ROS' | 'PE'
        items: { name: string; value: string }[]
        heading: string
        suppressHeading: boolean
      }

  const allEntries: SortableEntry[] = [...grouped.keys()].map((category) => ({
    kind: 'bullets' as const,
    category,
  }))

  // Track which categories already have bullet entries so we can suppress
  // duplicate headings when a checklist for the same category also exists.
  const categoriesWithBullets = new Set(grouped.keys())

  if (checklists.ros?.length) {
    allEntries.push({
      kind: 'checklist',
      category: 'ROS',
      items: checklists.ros,
      heading: 'Review of Systems',
      suppressHeading: categoriesWithBullets.has('ROS'),
    })
  }
  if (checklists.pe?.length) {
    allEntries.push({
      kind: 'checklist',
      category: 'PE',
      items: checklists.pe,
      heading: 'Physical Exam',
      suppressHeading: categoriesWithBullets.has('PE'),
    })
  }

  // For twoCol.elationNote, ROS/PE are single text-block bullets with
  // "- System: findings" lines instead of structured checklist items.
  // Parse them into checklist entries for consistent italicized rendering.
  if (template === 'twoCol.elationNote') {
    for (const [cat, heading] of [
      ['ROS', 'Review of Systems'],
      ['PE', 'Physical Exam'],
    ] as const) {
      const catBullets = grouped.get(cat)
      if (!catBullets?.length) {
        continue
      }
      const text = catBullets.map((b) => b.text ?? '').join('\n')
      const items: { name: string; value: string }[] = []
      let currentLabel = ''
      let currentValue = ''
      for (const line of text.split('\n')) {
        const trimmed = line.trim().replace(/^-\s*/, '')
        const match = /^(\w+):\s*(.*)/.exec(trimmed)
        if (match) {
          if (currentLabel) {
            items.push({ name: currentLabel, value: currentValue.trim() })
          }
          currentLabel = match[1]
          currentValue = match[2]
        } else if (trimmed && currentLabel) {
          currentValue += ` ${trimmed}`
        }
      }
      if (currentLabel) {
        items.push({ name: currentLabel, value: currentValue.trim() })
      }
      if (items.length > 0) {
        // Remove the bullet entry and replace with a checklist entry
        const bulletIdx = allEntries.findIndex(
          (e) => e.kind === 'bullets' && e.category === cat
        )
        if (bulletIdx !== -1) {
          allEntries.splice(bulletIdx, 1)
        }
        grouped.delete(cat)
        allEntries.push({
          kind: 'checklist',
          category: cat,
          items,
          heading,
          suppressHeading: false,
        })
      }
    }
  }

  const isTwoCol = template !== undefined && TWO_COL_TEMPLATES.has(template)
  const isElationNote = template === 'twoCol.elationNote'

  // For twoCol.elationNote, Elation sends content twice: once in column
  // categories (ElationNoteColOne/ColTwo) and once in standard categories
  // (Hpi, Med, PE, etc.). Use standard categories (better mention resolution)
  // and suppress the column duplicates. Keep ElationNoteColTwo vitals bullets
  // (BP, HR, Temp, etc.) inline since the structured vitals API is empty for
  // this template. Suppress ElationNoteOtherData (duplicate vitals values).
  if (isElationNote) {
    // Remove ElationNoteColOne entirely (duplicates standard categories)
    const col1Idx = allEntries.findIndex(
      (e) => e.kind === 'bullets' && e.category === 'ElationNoteColOne'
    )
    if (col1Idx !== -1) {
      allEntries.splice(col1Idx, 1)
    }
    grouped.delete('ElationNoteColOne')

    // Remove ElationNoteOtherData (duplicate vitals values)
    const otherIdx = allEntries.findIndex(
      (e) => e.kind === 'bullets' && e.category === 'ElationNoteOtherData'
    )
    if (otherIdx !== -1) {
      allEntries.splice(otherIdx, 1)
    }
    grouped.delete('ElationNoteOtherData')

    // Filter ElationNoteColTwo to vitals-only bullets (suppress PE, A&P, Data
    // which duplicate standard categories)
    const col2Bullets = grouped.get('ElationNoteColTwo')
    if (col2Bullets) {
      const VITALS_HEADERS = new Set([
        'Blood Pressure:',
        'Heart Rate:',
        'Temperature:',
        'Respiratory Rate:',
        'Height (in):',
        'BMI:',
        'O2:',
        'Pain:',
        'Weight:',
        'Weight (lbs):',
      ])
      const vitalsOnly = col2Bullets.filter((b) =>
        VITALS_HEADERS.has((b.text ?? '').trim())
      )
      if (vitalsOnly.length > 0) {
        grouped.set('ElationNoteColTwo', vitalsOnly)
      } else {
        const idx = allEntries.findIndex(
          (e) => e.kind === 'bullets' && e.category === 'ElationNoteColTwo'
        )
        if (idx !== -1) {
          allEntries.splice(idx, 1)
        }
        grouped.delete('ElationNoteColTwo')
      }
    }
  }

  // For two-column templates: split into left/right/below buckets first, then
  // sort each bucket independently. This decouples column assignment from sort
  // order so that TEMPLATE_SORT_ORDERS numbers are relative to column peers only.
  //
  // For single-column templates: all entries go into one bucket (leftEntries),
  // sorted globally — behavior unchanged.
  let leftEntries: SortableEntry[]
  let rightEntries: SortableEntry[]
  let belowEntries: SortableEntry[]

  if (isTwoCol) {
    leftEntries = []
    rightEntries = []
    belowEntries = []

    for (const entry of allEntries) {
      if (TWO_COL_BELOW_CATEGORIES.has(entry.category)) {
        belowEntries.push(entry)
      } else if (
        entry.kind === 'checklist'
          ? entry.category === 'PE'
          : TWO_COL_RIGHT_CATEGORIES.has(entry.category)
      ) {
        rightEntries.push(entry)
      } else {
        leftEntries.push(entry)
      }
    }

    const sortByTemplate = (a: SortableEntry, b: SortableEntry) =>
      getCategorySortOrder(a.category, template) -
      getCategorySortOrder(b.category, template)

    leftEntries.sort(sortByTemplate)
    rightEntries.sort(sortByTemplate)
    belowEntries.sort(sortByTemplate)
  } else {
    allEntries.sort(
      (a, b) =>
        getCategorySortOrder(a.category, template) -
        getCategorySortOrder(b.category, template)
    )
    leftEntries = allEntries
    rightEntries = []
    belowEntries = []
  }

  // Accumulate nodes into separate left/right/below arrays.
  // For single-column templates, all nodes go into leftNodes (used as the flat list).
  const leftNodes: TipTapBlockNode[] = []
  const rightObjectiveNodes: TipTapBlockNode[] = []
  const rightPlanNodes: TipTapBlockNode[] = []
  // Nodes rendered full-width below the two-column area (e.g. Followup).
  const belowNodes: TipTapBlockNode[] = []
  const nodes = leftNodes

  // In two-column mode, split right entries into Objective and Plan buckets
  // so we can insert a "Plan" H1 header between them.
  let rightObjectiveEntries: SortableEntry[] = []
  let rightPlanEntries: SortableEntry[] = []
  if (isTwoCol) {
    rightObjectiveEntries = rightEntries.filter(
      (e) => !TWO_COL_PLAN_CATEGORIES.has(e.category)
    )
    rightPlanEntries = rightEntries.filter((e) =>
      TWO_COL_PLAN_CATEGORIES.has(e.category)
    )
  }

  // Process each bucket of sorted entries into its corresponding node array.
  const buckets: [SortableEntry[], TipTapBlockNode[]][] = isTwoCol
    ? [
        [leftEntries, leftNodes],
        [rightObjectiveEntries, rightObjectiveNodes],
        [rightPlanEntries, rightPlanNodes],
        [belowEntries, belowNodes],
      ]
    : [
        [leftEntries, leftNodes],
        [belowEntries, belowNodes],
      ]

  for (const [bucketEntries, targetNodes] of buckets) {
    for (const entry of bucketEntries) {
      if (entry.kind === 'checklist') {
        // ROS/PE checklist: heading + italic-label bullet list.
        // When the same category also has bullet entries, the heading is
        // already emitted by the bullet group — suppress it to avoid duplicates.
        if (!entry.suppressHeading) {
          targetNodes.push(buildHeadingNode(entry.heading))
        }
        targetNodes.push(buildChecklistBulletList(entry.items))
        continue
      }

      const category = entry.category
      const categoryBullets = grouped.get(category) ?? []

      // For twoCol.elationNote, ElationNoteColTwo contains vitals-only bullets
      // (after filtering above). Render each as "Label: Value" paragraph.
      if (isElationNote && category === 'ElationNoteColTwo') {
        targetNodes.push(buildHeadingNode('Vitals'))
        for (const bullet of categoryBullets) {
          const label = (bullet.text ?? '').replace(/:$/, '').trim()
          const children = (bullet.children ?? [])
            .filter(
              (c) =>
                !c.replaced_by_edit &&
                !c.replaced_by &&
                !c.deleted_date &&
                c.text
            )
            .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
          const value = children[0]?.text?.trim() ?? ''
          if (label && value) {
            targetNodes.push({
              type: 'paragraph',
              content: [
                { type: 'text', text: `${label}: `, marks: [{ type: 'bold' }] },
                { type: 'text', text: value },
              ],
            } satisfies TipTapParagraphNode)
          }
        }
        continue
      }

      const isSuppressed =
        SUPPRESSED_CATEGORIES.has(category) ||
        (isTwoCol &&
          !isElationNote &&
          (category === 'Problem' || category === 'Hpi'))
      const inlinePrefix = INLINE_PREFIX_CATEGORIES[category]
      const displayName = CATEGORY_DISPLAY_NAMES[category] ?? category

      // Add heading unless suppressed or rendered as an inline prefix.
      // In two-column mode, A/P categories get H1 treatment (with divider).
      if (!isSuppressed && !inlinePrefix) {
        const headingLevel =
          isTwoCol && AP_CONDITION_CATEGORIES.has(category) ? 1 : 2
        targetNodes.push(buildHeadingNode(displayName, headingLevel))
      }

      const isNumbered =
        NUMBERED_CATEGORIES.has(category) &&
        !(template && UNNUMBERED_TEMPLATES.has(template)) &&
        !(isElationNote && category === 'Med')
      const isBulleted =
        BULLETED_CATEGORIES.has(category) ||
        (isElationNote && category === 'Med')
      const listItems: TipTapListItemNode[] = []

      for (let i = 0; i < categoryBullets.length; i++) {
        const bullet = categoryBullets[i]
        if (!bullet) {
          continue
        }
        let bulletText = (bullet.text || bullet.note_document?.summary)?.trim()
        if (bulletText && bullet.note_document) {
          bulletText = bulletText.replace(ELATION_ORDER_PREFIXES, '$& ')
        }
        if (!bulletText) {
          continue
        }

        if (!firstBulletText) {
          firstBulletText = bulletText
            .split('\n')[0]
            ?.trim()
            ?.replace(/[^a-zA-Z0-9)]+$/, '')
        }

        // For inline-prefix categories, prepend the prefix to the first bullet
        const text =
          inlinePrefix && i === 0 ? `${inlinePrefix} ${bulletText}` : bulletText

        // Build the paragraph node for this bullet
        let paragraphNode: TipTapParagraphNode

        // Document bullets (note_document set) that are NOT order-type get a
        // direct mention node if a resolved DocumentReference reference is
        // available. Order-type document bullets (prescriptions etc.) flow
        // through splitTextWithMentions to be matched as order mentions.
        const isDocumentBullet =
          bullet.note_document != null &&
          !orderRefMap.has(bulletText.toLowerCase()) &&
          documentRefMap.has(bulletText.toLowerCase())

        if (isDocumentBullet) {
          const docRef = documentRefMap.get(bulletText.toLowerCase()) ?? ''
          paragraphNode = {
            type: 'paragraph',
            content: [buildMentionNode(docRef, bulletText)],
          }
        } else {
          // Split the bullet text into inline nodes, injecting mention nodes for
          // any conditionRefMap / orderRefMap keys found as substrings.
          const inlineNodes = splitTextWithMentions(
            text,
            conditionRefMap,
            orderRefMap
          )

          // In A/P sections, append ICD-10 code inline after the condition mention
          // node as " [K51.90]" — matching the preview rendering style.
          if (AP_CONDITION_CATEGORIES.has(category)) {
            const icdCode = conditionIcdMap.get(bulletText.toLowerCase())
            if (icdCode) {
              inlineNodes.push({
                type: 'text',
                text: `  [${icdCode}]`,
              } satisfies TipTapTextNode)
            }
          }

          paragraphNode = { type: 'paragraph', content: inlineNodes }
        }

        // Child bullets become a bullet list (skip replaced/deleted children)
        const childTexts = (bullet.children ?? [])
          .filter(
            (c) => !c.replaced_by_edit && !c.replaced_by && !c.deleted_date
          )
          .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
          .map((c) => c.text)
          .filter((t): t is string => !!t)

        if (isNumbered || isBulleted) {
          const itemContent: (TipTapParagraphNode | TipTapBulletListNode)[] = [
            paragraphNode,
          ]
          if (childTexts.length > 0) {
            itemContent.push(buildBulletList(childTexts))
          }
          listItems.push({ type: 'listItem', content: itemContent })
        } else {
          targetNodes.push(paragraphNode)
          if (childTexts.length > 0) {
            targetNodes.push(buildBulletList(childTexts))
          }
        }
      }

      if (listItems.length > 0) {
        targetNodes.push({
          type: isNumbered ? 'orderedList' : 'bulletList',
          content: listItems,
        })
      }
    }
  }

  if (isTwoCol) {
    // For twoCol.elationNote, skip Subjective/Objective/Plan H1 headers —
    // the column categories already contain their own subsection structure.
    const rightContent: TipTapBlockNode[] = isElationNote
      ? [...rightObjectiveNodes, ...rightPlanNodes]
      : [buildHeadingNode('Objective', 1), ...rightObjectiveNodes]
    if (!isElationNote && rightPlanNodes.length > 0) {
      rightContent.push(buildHeadingNode('Plan', 1), ...rightPlanNodes)
    }

    const columnBlock: TipTapColumnBlockNode = {
      type: 'columnBlock',
      attrs: { layout: 'equal' },
      content: [
        {
          type: 'column',
          content: isElationNote
            ? leftNodes
            : [buildHeadingNode('Subjective', 1), ...leftNodes],
        },
        {
          type: 'column',
          content: rightContent,
        },
      ],
    }
    return {
      doc: { type: 'doc', content: [columnBlock, ...belowNodes] },
      firstBulletText,
    }
  }

  // Pre-Op template is enabled but untested with real data. Add a notice so
  // clinicians alert engineering if they encounter one.
  if (template === 'Pre-Op') {
    nodes.unshift({
      type: 'paragraph',
      content: [
        {
          type: 'text',
          text: 'This note uses the Pre-Op template. If you see formatting issues, please alert the engineering team.',
          marks: [{ type: 'italic' }],
        },
      ],
    } satisfies TipTapParagraphNode)
  }

  return { doc: { type: 'doc', content: nodes }, firstBulletText }
}

export function toFhir({
  visitNote,
  patient,
  author,
  encounter,
  signedBy,
  observations,
  orders,
  conditions,
  documents,
  billing,
}: {
  visitNote: VisitNote
  patient: Reference<Patient>
  author: Reference<Practitioner>
  encounter: Reference<Encounter>
  signedBy?: Reference<Practitioner>
  observations?: Reference<Observation>[]
  /*
   * Order entries for the Orders section. Resolved orders have a `reference` field
   * pointing to a MedicationRequest or ServiceRequest. Unresolved orders (not yet
   * synced to Medplum) have only a `display` string from the bullet text.
   */
  orders?: (
    | Reference<MedicationRequest>
    | Reference<ServiceRequest>
    | { display: string }
  )[]
  /*
   * Condition entries for the Conditions section. Resolved conditions have a
   * `reference` field pointing to a Condition resource. Unresolved conditions
   * (not yet matched in Medplum) have only a `display` string from the bullet text.
   * An optional `icd10Code` string (e.g. "K51.90") may be included for display in
   * the A/P section of the TipTap content.
   */
  conditions?: ((Reference<Condition> | { display: string }) & {
    icd10Code?: string
  })[]
  /*
   * Document entries for the Documents section. Resolved documents have a
   * `reference` field pointing to a DocumentReference resource. Unresolved
   * documents (not yet synced to Medplum) have only a `display` string.
   * When provided, resolved entries also receive mention nodes in the TipTap
   * content section.
   */
  documents?: (Reference<DocumentReference> | { display: string })[]
  /*
   * Billing data for the Billing section. When provided, a billing section is
   * appended to the Composition. Billing data comes from a separate Elation
   * API endpoint (not the visit note JSON itself).
   */
  billing?: BillingSectionData
}): Composition {
  /*
   * Build lookup maps for mention node injection.
   * Condition bullets matched to a resolved Condition resource get a mention node
   * in the TipTap content with a "Condition/uuid" reference.
   * Order bullets matched to a resolved order get a mention with the order reference.
   */
  const conditionRefMap = new Map<string, string>()
  const conditionIcdMap = new Map<string, string>()
  for (const c of conditions ?? []) {
    if ('reference' in c && c.reference && c.display) {
      conditionRefMap.set(c.display.trim().toLowerCase(), c.reference)
    }
    if (c.display && c.icd10Code) {
      conditionIcdMap.set(c.display.trim().toLowerCase(), c.icd10Code)
    }
  }

  const orderRefMap = new Map<string, string>()
  for (const o of orders ?? []) {
    if ('reference' in o && o.reference && o.display) {
      orderRefMap.set(
        o.display.replace(ELATION_ORDER_PREFIXES, '').trim().toLowerCase(),
        o.reference
      )
    }
  }

  const documentRefMap = new Map<string, string>()
  for (const d of documents ?? []) {
    if ('reference' in d && d.reference && d.display) {
      documentRefMap.set(d.display.trim().toLowerCase(), d.reference)
    }
  }

  /*
   * isConditionBullet is used to identify condition bullets for the metadata Conditions
   * section (child sub-sections). Unlike the old code, we no longer exclude these
   * bullets from the TipTap content section — they appear as mention nodes or plain text.
   */
  const conditionDisplayTexts = new Set(
    (conditions ?? [])
      .map((c) => c.display?.toLowerCase())
      .filter((d): d is string => !!d)
  )

  const isConditionBullet = (b: Bullet): boolean =>
    CONDITION_BULLET_CATEGORIES.has(b.category) &&
    b.note_document === null &&
    !!b.text &&
    !b.replaced_by_edit &&
    !b.replaced_by &&
    !b.deleted_date &&
    conditionDisplayTexts.has(b.text.toLowerCase())

  /*
   * All bullets with text (or note_document.summary fallback) go into the
   * TipTap content section, except EXCLUDED_CATEGORIES (e.g. Orders) whose
   * content flows through dedicated metadata sections only.
   * (Condition bullets are NOT excluded — they become mention nodes.)
   */
  const contentBullets = visitNote.bullets.filter(
    (b) =>
      (!!b.text || !!b.note_document?.summary) &&
      !EXCLUDED_CATEGORIES.has(b.category) &&
      !b.replaced_by_edit &&
      !b.replaced_by &&
      !b.deleted_date
  )

  /*
   * Build the single TipTap JSON content section.
   */
  const { doc: tiptapDoc, firstBulletText: tiptapFirstBullet } =
    buildTipTapDocument({
      bullets: contentBullets,
      checklists: visitNote.checklists,
      conditionRefMap,
      orderRefMap,
      documentRefMap,
      conditionIcdMap,
      template: visitNote.template,
    })

  const section: CompositionSection[] = [
    {
      title: 'Content',
      code: {
        coding: [
          {
            system: codeSystems.VISIT_NOTE_CONTENT_SECTION_SYSTEM,
            code: 'content',
            display: 'Content',
          },
        ],
      },
      text: {
        status: 'additional',
        div: JSON.stringify(tiptapDoc),
      },
    } satisfies CompositionSection,
  ]

  /*
   * Observation sections — unchanged, one per observation reference.
   */
  observations?.forEach((observation) => {
    section.push({
      title: OBSERVATION_SECTION.display ?? 'Observations',
      code: {
        coding: [OBSERVATION_SECTION],
      },
      text: {
        status: 'additional',
        div: observation.display ?? '',
      },
      entry: [observation],
    } satisfies CompositionSection)
  })

  /*
   * Orders section — unchanged, aggregated single section with all order entries.
   */
  if (orders && orders.length > 0) {
    const displayNames = orders
      .map((o) => o.display)
      .filter((d): d is string => !!d)
    section.push({
      title: ORDER_SECTION.display ?? 'Orders',
      code: {
        coding: [ORDER_SECTION],
      },
      text: {
        status: displayNames.length > 0 ? 'additional' : 'empty',
        div:
          displayNames.length > 0
            ? `<div xmlns="http://www.w3.org/1999/xhtml">${displayNames.map((d) => d.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')).join(', ')}</div>`
            : '<div xmlns="http://www.w3.org/1999/xhtml"/>',
      },
      entry: orders,
    } satisfies CompositionSection)
  }

  /*
   * Conditions section — unchanged, single aggregated section with child sub-sections
   * for condition bullet children.
   */
  if (conditions && conditions.length > 0) {
    const displayNames = conditions
      .map((c) => c.display)
      .filter((d): d is string => !!d)

    /* Collect children from condition bullets as nested sub-sections */
    const conditionChildSections: CompositionSection[] = []
    for (const bullet of visitNote.bullets) {
      if (isConditionBullet(bullet) && bullet.children?.length) {
        for (const child of bullet.children) {
          if (
            child.text?.trim() &&
            !child.replaced_by_edit &&
            !child.replaced_by &&
            !child.deleted_date
          ) {
            conditionChildSections.push({
              title: bullet.text,
              text: {
                status: 'additional',
                div: child.text.trim(),
              },
            })
          }
        }
      }
    }

    section.push({
      title: CONDITION_SECTION.display ?? 'Conditions',
      code: { coding: [CONDITION_SECTION] },
      text: {
        status: displayNames.length > 0 ? 'additional' : 'empty',
        div:
          displayNames.length > 0
            ? `<div xmlns="http://www.w3.org/1999/xhtml">${displayNames.map((d) => d.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')).join(', ')}</div>`
            : '<div xmlns="http://www.w3.org/1999/xhtml"/>',
      },
      entry: conditions.map(({ icd10Code: _, ...ref }) => ref),
      section:
        conditionChildSections.length > 0 ? conditionChildSections : undefined,
    } satisfies CompositionSection)
  }

  /*
   * Document section — non-order documents (labs, consults, scans, etc.).
   * When `documents` is provided (from resolveVisitNoteDocumentRefs), use it
   * directly — it already excludes order-type documents and includes resolved
   * references. Otherwise fall back to filtering bullets (no order-type exclusion).
   */
  const documentEntries: Reference<DocumentReference>[] = documents
    ? documents.map((d) =>
        'reference' in d
          ? (d as Reference<DocumentReference>)
          : ({ display: d.display } satisfies Reference<DocumentReference>)
      )
    : visitNote.bullets
        .filter(
          (b) =>
            b.note_document?.document?.id != null &&
            (b.text || b.note_document.summary) &&
            !b.replaced_by_edit &&
            !b.replaced_by &&
            !b.deleted_date &&
            (b.note_document.document.document_type === null ||
              !ORDER_ENTITY_TYPES.has(b.note_document.document.document_type))
        )
        .map((b) => {
          const displayText = b.text || b.note_document?.summary || 'Document'
          const resolvedRef = documentRefMap.get(displayText.toLowerCase())
          return resolvedRef
            ? ({
                reference: resolvedRef,
                display: displayText,
              } satisfies Reference<DocumentReference>)
            : ({ display: displayText } satisfies Reference<DocumentReference>)
        })

  if (documentEntries.length > 0) {
    section.push({
      title: DOCUMENT_SECTION.display ?? 'Documents',
      code: { coding: [DOCUMENT_SECTION] },
      entry: documentEntries,
    } satisfies CompositionSection)
  }

  /*
   * Reason section — creates a metadata section for header display.
   * Reason bullets are excluded from TipTap content (see EXCLUDED_CATEGORIES),
   * so they must be captured here as a dedicated section. Matches prod format:
   * { title: "Reason", text: { status: "additional", div: "bullet1; bullet2" } }
   */
  const reasonBullets = visitNote.bullets.filter(
    (b) =>
      b.category === 'Reason' &&
      b.text &&
      !b.replaced_by_edit &&
      !b.replaced_by &&
      !b.deleted_date
  )
  if (reasonBullets.length > 0) {
    section.push({
      title: 'Reason',
      text: {
        status: 'additional',
        div: reasonBullets
          .map((b) => b.text)
          .join('; ')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;'),
      },
    } satisfies CompositionSection)
  }

  /*
   * ROS and PE checklists are rendered inline in the TipTap content section.
   * No separate metadata sections needed — nothing reads them.
   */

  /*
   * Billing section — stores billing data as JSON in text.div.
   * Only emitted when billing data is provided (from Elation billing endpoint).
   */
  if (billing) {
    section.push({
      title: BILLING_SECTION.display ?? 'Billing',
      code: { coding: [BILLING_SECTION] },
      text: {
        status: 'additional',
        div: JSON.stringify(billing),
      },
    } satisfies CompositionSection)
  }

  const seenVisitNote = new SeenVisitNote()
  if (signedBy && visitNote.signed_date) {
    seenVisitNote.sign(signedBy, visitNote.signed_date)
  }

  seenVisitNote.elationId = visitNote.id ?? undefined
  seenVisitNote.status = visitNote.signed_date ? 'final' : 'preliminary'
  seenVisitNote.subject = patient
  seenVisitNote.author = [author]
  seenVisitNote.date = visitNote.created_date
  const reasonText = reasonBullets
    .map((b) => b.text?.trim())
    .filter(Boolean)
    .join(', ')
  const titleSuffix = reasonText || tiptapFirstBullet || visitNote.template
  const MAX_SUFFIX_LENGTH = 80
  let truncatedSuffix = titleSuffix
  if (titleSuffix.length > MAX_SUFFIX_LENGTH) {
    const cut = titleSuffix.lastIndexOf(' ', MAX_SUFFIX_LENGTH)
    truncatedSuffix = `${titleSuffix.slice(0, cut > 0 ? cut : MAX_SUFFIX_LENGTH)}…`
  }
  seenVisitNote.title = `${visitNote.type} — ${truncatedSuffix}`
  seenVisitNote.encounter = encounter
  seenVisitNote.sections = section
  seenVisitNote.type = {
    coding: [
      {
        system: codeSystems.ELATION_VISIT_NOTE_TEMPLATE_SYSTEM,
        code: visitNote.template,
        display: visitNote.type,
      },
    ],
  }
  seenVisitNote.externalResourceJSON = JSON.stringify(visitNote)
  return seenVisitNote.toFhirResource()
}
